import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ReactReader } from 'react-reader';
import { Paper, Typography, Box, CircularProgress, Button } from '@mui/material';
import { useBook } from '../../Context/BookContext';
import { Refresh } from '@mui/icons-material';
import { useAuth } from '../../Context/AuthContext';
import { getPresignedBookUrl } from '../../utils/api';

const getPresignedUrlEndpoint = process.env.REACT_APP_LAMBDA_GET_PRESIGNED_URL_BOOK;

const BookReaderPage: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const { selectedBook } = useBook();
  const [location, setLocation] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [presignedUrl, setPresignedUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [urlFetchTime, setUrlFetchTime] = useState<number | null>(null);
  const renditionRef = useRef<any>(null);

  const getPresignedUrl = useCallback(async (): Promise<void> => {
    if (!selectedBook || !getPresignedUrlEndpoint || !isAuthenticated || !user?.userId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Do not pass a version; backend will return latest illustrated if available
      const { presignedUrl } = await getPresignedBookUrl(selectedBook.FileId);
      setPresignedUrl(presignedUrl);
      setUrlFetchTime(Date.now());
    } catch (error: any) {
      console.error('Error getting presigned URL:', error);
      
      // Handle specific error cases
      if (error.response?.status === 404) {
        setError('Book file not found in storage. The file may have been deleted or corrupted. Please try re-uploading the book.');
      } else if (error.response?.status === 403) {
        setError('Access denied. You do not have permission to access this book.');
      } else {
        setError('Failed to get book access URL. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [selectedBook, isAuthenticated, user?.userId]);

  // Check if presigned URL is still valid (expires in 1 hour)
  const isUrlValid = useCallback((): boolean => {
    if (!presignedUrl || !urlFetchTime) return false;
    const now = Date.now();
    const timeElapsed = now - urlFetchTime;
    
    // Consider URL invalid if more than 50 minutes have passed (safety buffer)
    return timeElapsed < (50 * 60 * 1000);
  }, [presignedUrl, urlFetchTime]);

  // Auto-refresh URL if it's about to expire
  const ensureValidUrl = useCallback(async (): Promise<void> => {
    if (!isUrlValid()) {
      console.log('Presigned URL expired or about to expire, refreshing...');
      await getPresignedUrl();
    }
  }, [isUrlValid, getPresignedUrl]);

  useEffect(() => {
    if (selectedBook) {
      getPresignedUrl();
    }
  }, [selectedBook, getPresignedUrl]);

  // Check URL validity when page becomes visible (user switches tabs/windows)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && presignedUrl) {
        ensureValidUrl();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [presignedUrl, ensureValidUrl]);

  // Callback when the book is loaded
  const handleReaderLoad = (rendition: any): void => {
    renditionRef.current = rendition;
    setIsLoading(false);
  };

  // Callback when the user changes pages/location
  const handleLocationChanged = (epubcfi: string): void => {
    setLocation(epubcfi);
    // You can save this location to resume reading later
  };

  if (!selectedBook) {
    return (
      <Typography variant="h6" color="textSecondary">
        Please select a book from the Library first
      </Typography>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={getPresignedUrl}
          sx={{ mt: 2 }}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <div>
      <Box 
        display="flex" 
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between" 
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        gap={2}
        mb={2}
      >
        <Typography variant="h4" sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
          Book Reader
        </Typography>
        <Button 
          variant="contained"
          color="primary"
          startIcon={<Refresh />}
          onClick={getPresignedUrl}
          disabled={isLoading}
          fullWidth={false}
          sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
        >
          Refresh Book
        </Button>
      </Box>
      
      <Paper sx={{ p: { xs: 2, md: 3 } }}>
        <Box 
          display="flex" 
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between" 
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          gap={1}
          mb={2}
        >
          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
            {selectedBook.Title || selectedBook.FileName}
          </Typography>
          {isLoading && <CircularProgress size={24} />}
        </Box>
        
        {presignedUrl && isUrlValid() && (
          <Box sx={{ height: '70vh', position: 'relative' }}>
            <ReactReader
              url={presignedUrl}
              title={selectedBook.Title || selectedBook.FileName}
              location={location}
              locationChanged={handleLocationChanged}
              getRendition={handleReaderLoad}
              epubOptions={{
                allowScriptedContent: true
              }}
            />
          </Box>
        )}
        
        {presignedUrl && !isUrlValid() && !isLoading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography variant="body1" color="textSecondary">
              Refreshing book access...
            </Typography>
          </Box>
        )}
      </Paper>
    </div>
  );
};

export default BookReaderPage; 